<template>
  <div class="intent-forced-extraction">
    <section class="forced-section">
      <div class="forced-section__head">
        <div class="forced-section__title-wrap">
          <span class="forced-section__badge">1</span>
          <span class="forced-section__title">信息采集</span>
        </div>
        <Button type="text" class="forced-section__add" @click="openFieldModal(-1)">
          + 添加字段
        </Button>
      </div>

      <div class="forced-form-name-row">
        <div class="forced-form-name-row__main">
          <span class="forced-form-name-row__label">信息采集名称</span>
          <div class="forced-form-name-row__value-wrap">
            <span class="forced-form-name-row__value">{{ displayExtractFormName }}</span>
            <EditIconButton size="sm" title="编辑" @click="openFormNameModal" />
          </div>
        </div>
      </div>

      <div v-if="extractFields.length" class="forced-field-list">
        <div
          v-for="(field, index) in extractFields"
          :key="field.fieldId || 'field-' + index"
          class="forced-field-item"
        >
          <div class="forced-field-item__main">
            <div class="forced-field-item__lines">
              <div class="forced-field-item__title-row">
                <span class="forced-field-item__name">{{ field.name || '—' }}</span>
                <span class="forced-field-item__type-tag">{{ getFieldTypeShortLabel(field.type) }}</span>
              </div>
              <div class="forced-field-item__values-row">
                <span class="forced-field-item__values-label">是否在表单展示</span>
                <span>{{ isCollectFormField(field) ? '是' : '否' }}</span>
              </div>
              <div class="forced-field-item__values-row">
                <template v-if="field.type === EXTRACT_FIELD_TYPE.ENUM">
                  <span class="forced-field-item__values-label">允许值</span>
                  <span
                    v-for="(val, valIndex) in field.allowedValues"
                    :key="'val-' + valIndex + '-' + val"
                    class="forced-field-item__value-chip"
                  >{{ val }}</span>
                </template>
                <span v-else class="forced-field-item__no-enum">无枚举限制</span>
              </div>
              <div v-if="field.extractInstruction" class="forced-field-item__desc">{{ field.extractInstruction }}</div>
            </div>
          </div>
          <div class="forced-field-item__actions">
            <template v-if="!isSystemField(field)">
              <EditIconButton
                size="sm"
                title="编辑"
                @click="openFieldModal(index)"
              />
              <Poptip
                :value="exclusiveConfirmKey === 'forced-field-' + index"
                confirm
                placement="top-end"
                transfer
                :title="fieldDeleteTitle(field)"
                ok-text="确定"
                cancel-text="取消"
                @click.native.stop
                @input="onExclusiveConfirmInput('forced-field-' + index, $event)"
                @on-ok="removeField(index)"
              >
                <span class="forced-field-del-trigger">
                  <DeleteIconButton size="sm" title="删除" />
                </span>
              </Poptip>
            </template>
            <span v-else class="forced-field-item__system-tag">系统</span>
          </div>
        </div>
      </div>
      <div v-else class="forced-empty">暂无采集字段，点击右上角添加</div>
    </section>

    <section class="forced-section">
      <div class="forced-section__head">
        <div class="forced-section__title-wrap">
          <span class="forced-section__badge">2</span>
          <span class="forced-section__title">评级规则</span>
        </div>
        <Button type="text" class="forced-section__add" @click="openRuleModal(-1)">
          + 添加规则
        </Button>
      </div>

      <draggable
        v-if="ratingRules.length"
        v-model="ratingRules"
        handle=".forced-rule-card__drag"
        class="forced-rule-list"
        :animation="180"
        ghost-class="forced-rule-card--ghost"
        chosen-class="forced-rule-card--chosen"
        @end="resetRatingRankings"
      >
        <div
          v-for="(rule, index) in ratingRules"
          :key="'rule-' + index + '-' + (rule.name || '')"
          class="forced-rule-card"
        >
          <span class="forced-rule-card__drag" title="拖拽排序">
            <span class="forced-rule-card__drag-dot" />
            <span class="forced-rule-card__drag-dot" />
            <span class="forced-rule-card__drag-dot" />
            <span class="forced-rule-card__drag-dot" />
            <span class="forced-rule-card__drag-dot" />
            <span class="forced-rule-card__drag-dot" />
          </span>
          <span class="forced-rule-card__rank">{{ index + 1 }}</span>
          <div class="forced-rule-card__main">
            <div class="forced-rule-card__name">{{ rule.name || '—' }}</div>
            <div class="forced-rule-card__logic">{{ formatRuleLogic(rule) }}</div>
          </div>
          <div class="forced-rule-card__actions">
            <EditIconButton size="sm" title="编辑" @click="openRuleModal(index)" />
            <Poptip
              :value="exclusiveConfirmKey === 'forced-rule-' + index"
              confirm
              placement="top-end"
              transfer
              :title="ruleDeleteTitle(rule)"
              ok-text="确定"
              cancel-text="取消"
              @click.native.stop
              @input="onExclusiveConfirmInput('forced-rule-' + index, $event)"
              @on-ok="removeRule(index)"
            >
              <span class="forced-field-del-trigger">
                <DeleteIconButton size="sm" title="删除" />
              </span>
            </Poptip>
          </div>
        </div>
      </draggable>
      <div v-else class="forced-empty">暂无评级规则，点击右上角添加</div>
    </section>

    <CollectionFieldModal
      v-model="fieldModalVisible"
      :edit-index="fieldEditIndex"
      :initial-data="fieldEditData"
      @submit="onFieldSubmit"
    />
    <RatingRuleModal
      v-model="ruleModalVisible"
      :edit-index="ruleEditIndex"
      :initial-data="ruleEditData"
      :extract-fields="extractFields"
      @submit="onRuleSubmit"
    />

    <Modal
      v-model="formNameModalVisible"
      title="编辑信息采集名称"
      class-name="intent-extract-form-name-modal"
      width="520"
    >
      <div class="intent-modal-form">
        <div class="intent-modal-field">
          <label class="intent-modal-field__label">
            <span class="intent-modal-field__required">*</span>
            名称
          </label>
          <Input
            v-model="formNameDraft"
            class="intent-modal-field__input"
            :maxlength="EXTRACT_FORM_NAME_MAX_LENGTH"
            placeholder="如：话术名称-采集表单"
            @on-enter="saveFormName"
          />
        </div>
      </div>

      <div slot="footer" class="intent-modal-footer">
        <Button @click="closeFormNameModal">取消</Button>
        <Button type="primary" @click="saveFormName">保存</Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import EditIconButton from '../shared/EditIconButton.vue'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import CollectionFieldModal from './CollectionFieldModal.vue'
import RatingRuleModal from './RatingRuleModal.vue'
import { EXTRACT_FIELD_TYPE, EXTRACT_FORM_NAME_MAX_LENGTH } from '../../constants/intentTag'
import {
  ensureForcedSystemExtractFields,
  formatRatingRuleLogic,
  getExtractFieldTypeShortLabel,
  isExtractFieldCollectForm,
  isSystemFieldId,
  sortRatingRulesByRanking,
  resolveExtractFormName,
} from '../../utils/intentTagHelpers'
import { exclusiveConfirmMixin } from '../../utils/exclusiveConfirm'

export default {
  name: 'IntentForcedExtractionPanel',
  mixins: [exclusiveConfirmMixin],
  components: {
    draggable,
    EditIconButton,
    DeleteIconButton,
    CollectionFieldModal,
    RatingRuleModal,
  },
  props: {
    intentTag: {
      type: Object,
      required: true,
    },
    sceneName: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      EXTRACT_FIELD_TYPE,
      EXTRACT_FORM_NAME_MAX_LENGTH,
      fieldModalVisible: false,
      fieldEditIndex: -1,
      fieldEditData: null,
      ruleModalVisible: false,
      ruleEditIndex: -1,
      ruleEditData: null,
      formNameModalVisible: false,
      formNameDraft: '',
    }
  },
  computed: {
    displayExtractFormName() {
      const name = resolveExtractFormName(this.intentTag, this.sceneName)
      return name || '—'
    },
    extractFields() {
      return Array.isArray(this.intentTag.extractFields) ? this.intentTag.extractFields : []
    },
    ratingRules: {
      get() {
        return Array.isArray(this.intentTag.ratingRules) ? this.intentTag.ratingRules : []
      },
      set(list) {
        this.$set(this.intentTag, 'ratingRules', Array.isArray(list) ? list : [])
        this.resetRatingRankings()
      },
    },
  },
  mounted() {
    this.ensureArrays()
  },
  methods: {
    ensureArrays() {
      if (this.intentTag.extractFormName == null) {
        this.$set(this.intentTag, 'extractFormName', '')
      }
      if (this.intentTag.extractFormId == null) {
        this.$set(this.intentTag, 'extractFormId', '')
      }
      if (!Array.isArray(this.intentTag.extractFields)) {
        this.$set(this.intentTag, 'extractFields', [])
      }
      if (!Array.isArray(this.intentTag.ratingRules)) {
        this.$set(this.intentTag, 'ratingRules', [])
      }
      sortRatingRulesByRanking(this.intentTag.ratingRules)
      this.resetRatingRankings()
      ensureForcedSystemExtractFields(this.intentTag)
    },
    resetRatingRankings() {
      const rules = Array.isArray(this.intentTag.ratingRules) ? this.intentTag.ratingRules : []
      rules.forEach((rule, index) => {
        if (rule && typeof rule === 'object') {
          this.$set(rule, 'ranking', index + 1)
        }
      })
    },
    openFormNameModal() {
      this.formNameDraft = resolveExtractFormName(this.intentTag, this.sceneName)
      this.formNameModalVisible = true
    },
    closeFormNameModal() {
      this.formNameModalVisible = false
    },
    saveFormName() {
      const name = this.formNameDraft != null ? String(this.formNameDraft).trim() : ''
      if (!name) {
        this.$Message.warning('请输入信息采集名称')
        return
      }
      this.$set(this.intentTag, 'extractFormName', name)
      this.formNameModalVisible = false
    },
    getFieldTypeShortLabel(type) {
      return getExtractFieldTypeShortLabel(type)
    },
    isCollectFormField(field) {
      return isExtractFieldCollectForm(field)
    },
    isSystemField(field) {
      return !!(field && isSystemFieldId(field.fieldId))
    },
    formatRuleLogic(rule) {
      return formatRatingRuleLogic(rule, this.extractFields)
    },
    openFieldModal(index) {
      const row = this.extractFields[index]
      if (row && isSystemFieldId(row.fieldId)) {
        this.$Message.warning('系统指标字段不可编辑')
        return
      }
      this.fieldEditIndex = index
      this.fieldEditData = index >= 0 ? { ...this.extractFields[index] } : null
      this.fieldModalVisible = true
    },
    onFieldSubmit(payload) {
      this.ensureArrays()
      if (this.fieldEditIndex >= 0) {
        const prev = this.extractFields[this.fieldEditIndex]
        if (prev && isSystemFieldId(prev.fieldId)) {
          this.$Message.warning('系统指标字段不可编辑')
          return
        }
        this.$set(this.intentTag.extractFields, this.fieldEditIndex, payload)
      } else {
        this.intentTag.extractFields.push(payload)
      }
    },
    removeField(index) {
      const removed = this.extractFields[index]
      if (removed && isSystemFieldId(removed.fieldId)) {
        this.$Message.warning('系统指标字段不可删除')
        return
      }
      this.closeExclusiveConfirm('forced-field-' + index)
      this.intentTag.extractFields.splice(index, 1)
      if (!removed) return
      this.ratingRules.forEach((rule) => {
        if (!rule || !Array.isArray(rule.conditions)) return
        rule.conditions = rule.conditions.filter((cond) => cond.fieldId !== removed.fieldId)
      })
    },
    fieldDeleteTitle(field) {
      const name = field && field.name ? String(field.name).trim() : ''
      return name ? `确定删除字段「${name}」吗？` : '确定删除该字段吗？'
    },
    openRuleModal(index) {
      this.ruleEditIndex = index
      this.ruleEditData =
        index >= 0
          ? {
              name: this.ratingRules[index].name,
              conditions: (this.ratingRules[index].conditions || []).map((item) => ({
                ...item,
              })),
            }
          : null
      this.ruleModalVisible = true
    },
    onRuleSubmit(payload) {
      this.ensureArrays()
      if (this.ruleEditIndex >= 0) {
        this.$set(this.intentTag.ratingRules, this.ruleEditIndex, {
          ...payload,
          ranking: this.ruleEditIndex + 1,
        })
      } else {
        this.intentTag.ratingRules.push({
          ...payload,
          ranking: this.ratingRules.length + 1,
        })
      }
      this.resetRatingRankings()
    },
    removeRule(index) {
      this.closeExclusiveConfirm('forced-rule-' + index)
      this.intentTag.ratingRules.splice(index, 1)
      this.resetRatingRankings()
    },
    ruleDeleteTitle(rule) {
      const name = rule && rule.name ? String(rule.name).trim() : ''
      return name ? `确定删除规则「${name}」吗？` : '确定删除该规则吗？'
    },
  },
}
</script>

<style lang="scss" scoped>
.intent-forced-extraction {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.forced-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
  text-align: left;
}

.forced-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.forced-form-name-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c5ccf8;
    box-shadow: 0 1px 4px rgba(86, 104, 240, 0.06);
  }
}

.forced-form-name-row__main {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.forced-form-name-row__value-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
}

.forced-form-name-row__label {
  flex-shrink: 0;
  font-size: 13px;
  color: #64748b;
}

.forced-form-name-row__value {
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
  word-break: break-word;
}

.forced-section__title-wrap {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.forced-section__badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding-top: 1px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #5668f0;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
}

.forced-section__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
}

.forced-section__add {
  padding: 4px 8px !important;
  height: auto !important;
  color: #5668f0 !important;
  font-size: 14px !important;
  border-radius: 4px;

  &:hover,
  &:focus {
    color: #3d52e8 !important;
    background: #eef1ff !important;
  }
}

.forced-empty {
  padding: 28px 16px;
  text-align: center;
  font-size: 13px;
  color: #8c8c8c;
  background: #fafafa;
  border: 1px dashed #e8eaec;
  border-radius: 6px;
}

.forced-field-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.forced-field-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  text-align: left;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c5ccf8;
    box-shadow: 0 1px 4px rgba(86, 104, 240, 0.06);
  }
}

.forced-field-item__main {
  flex: 1;
  min-width: 0;
}

.forced-field-item__lines {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  gap: 8px;
}

.forced-field-item__title-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
}

.forced-field-item__name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
}

.forced-field-item__type-tag {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 500;
  line-height: 20px;
  color: #475569;
  background: #f0f3f9;
  border: none;
  border-radius: 4px;
  flex-shrink: 0;
}

.forced-field-item__values-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  font-size: 13px;
  line-height: 1.5;
  color: #64748b;
}

.forced-field-item__values-label {
  color: #94a3b8;
  flex-shrink: 0;
  margin-right: 2px;
}

.forced-field-item__value-chip {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  font-size: 12px;
  line-height: 22px;
  color: #5668f0;
  background: #eef1ff;
  border: none;
  border-radius: 4px;
  box-sizing: border-box;
}

.forced-field-item__no-enum {
  font-size: 13px;
  color: #94a3b8;
  line-height: 1.5;
}

.forced-field-item__desc {
  margin: 0;
  padding: 0;
  width: 100%;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
  text-align: left;
  white-space: pre-wrap;
  word-break: break-word;
}

.forced-field-item__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.forced-field-del-trigger {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

.forced-field-item__system-tag {
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 8px;
  border-radius: 4px;
  background: #f5f5f5;
  color: #8c8c8c;
  font-size: 12px;
  line-height: 1;
}

.forced-rule-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.forced-rule-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #c5ccf8;
    box-shadow: 0 1px 4px rgba(86, 104, 240, 0.06);
  }
}

.forced-rule-card__drag {
  cursor: grab;
  display: grid;
  grid-template-columns: repeat(2, 4px);
  grid-template-rows: repeat(3, 4px);
  gap: 0;
  flex-shrink: 0;
  padding: 4px 2px;
  align-self: center;

  &:active {
    cursor: grabbing;
  }
}

.forced-rule-card__drag-dot {
  width: 2px;
  height: 2px;
  border-radius: 50%;
  background: #bfbfbf;
}

.forced-rule-card--ghost {
  opacity: 0.55;
}

.forced-rule-card--chosen .forced-rule-card__drag {
  cursor: grabbing;
}

.forced-rule-card__rank {
  flex-shrink: 0;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #eef0ff;
  color: #5668f0;
  font-size: 12px;
  font-weight: 600;
  line-height: 24px;
  text-align: center;
}

.forced-rule-card__main {
  flex: 1;
  min-width: 0;
}

.forced-rule-card__name {
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  line-height: 1.5;
}

.forced-rule-card__logic {
  margin-top: 4px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
  word-break: break-word;
}

.forced-rule-card__actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.intent-modal-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.intent-modal-field {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
}

.intent-modal-field__label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  line-height: 1.5;
  color: #262626;
  font-weight: 400;
}

.intent-modal-field__required {
  margin-right: 2px;
  color: #ed4014;
}

.intent-modal-field__input {
  width: 100%;
}

.intent-modal-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
}
</style>

<style lang="scss">
.intent-extract-form-name-modal {
  .ivu-modal-body {
    padding: 20px 24px 8px;
  }

  .ivu-modal-footer {
    border-top: none;
    padding: 8px 24px 20px;

    button + button {
      margin-left: 0;
    }
  }
}
</style>
